import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../parts/SvgIcon";
import TeamTabs from "./components/TeamTabs";
import submit from "submit-form";
import TeamProgramManager from "./components/TeamProgramManager";
import TeamInformation from "./components/TeamInformation";
import TeamMembers from "./components/TeamMembers";
import TeamOverview from "./components/TeamOverview";
import { getTeamConversation } from "../../../utils/chat-util";
import { isPullActiveCoach } from "../../../utils/user-helpers";
import ToolTip from "../../../parts/tooltip";

const Teams = ({ locals, api }) => {
  const { t } = useTranslation("coaching-portal");
  const cycle = locals?.editCycle;
  const query = locals?.query;
  const { pushAccountInfo, pressAccountInfo, pullAccountInfo, referAsClients } =
    locals?.activeUserToRequest || {};
  const userCanAccessToCommunityChat =
    pushAccountInfo?.inAppCommunityEnabled ||
    pressAccountInfo?.inAppCommunityEnabled ||
    pullAccountInfo?.inAppCommunityEnabled;

  const coachIsPullForRealsies = isPullActiveCoach(locals?.activeUserToRequest);

  const [createChatButton, setCreateChat] = useState(false);
  const [teamChatId, setTeamChatId] = useState(null);

  const fromTemplates =
    (locals?.pathname || locals?.path || "").includes("/templates/teams") ||
    query?.fromTemplate === "true";

  const backPath = fromTemplates
    ? `/coaching-portal/all-templates?owner=coach&tabView=teams-programs-tab&templateView=teams-programs&status=${cycle?.status ?? "published"}`
    : `/coaching-portal/store?storeType=teams&status=${cycle?.status ?? "published"}`;
  let organizationQuery = ``;

  if (query?.organizationCoach) {
    organizationQuery = `&organizationCoach=${query.organizationCoach}`;
  }

  const currentTab = query?.currentTab || "manager";
  const cycleId = cycle?._id ? String(cycle._id) : null;
  const cycleStatus = cycle?.status ?? null;
  const coachId = locals?.activeUserToRequest?._id
    ? String(locals.activeUserToRequest._id)
    : null;

  // Keyed on team, coach, and whether chat can exist. api is omitted so a new
  // locals object does not refetch. Stale responses are dropped via cancelled.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on ids and chat availability
  useEffect(() => {
    let cancelled = false;
    setTeamChatId(null);
    setCreateChat(false);

    if (
      !userCanAccessToCommunityChat ||
      cycleStatus !== "published" ||
      !cycleId ||
      !coachId
    ) {
      return;
    }

    const lookupTeamChat = async () => {
      try {
        const result = await getTeamConversation(api, cycleId, coachId);
        if (cancelled) return;

        if (!result) {
          setTeamChatId(null);
          setCreateChat(true);
          return;
        }

        setCreateChat(false);
        setTeamChatId(result.id ? String(result.id) : null);
      } catch {
        if (cancelled) return;
        setTeamChatId(null);
        setCreateChat(false);
      }
    };

    lookupTeamChat();

    return () => {
      cancelled = true;
    };
  }, [coachId, cycleId, cycleStatus, userCanAccessToCommunityChat]);

  return (
    <main className="teams-manager">
      <header>
        <hgroup>
          <a href={backPath + organizationQuery}>
            <button data-button-type="tertiary" data-icon-only>
              <SvgIcon src={"/img/new-icons/arrow-square-left.svg"} />
            </button>
            <span className="text-nowrap pr-4">{cycle?.name}</span>
          </a>
        </hgroup>

        <hgroup>
          {coachIsPullForRealsies &&
            (cycle.status !== "published" ? (
              <ToolTip
                id="invite-button-tooltip-disabled"
                body={
                  referAsClients
                    ? t("teams.inviteTooltipClient")
                    : t("teams.inviteTooltipAthlete")
                }
                place="top"
              >
                <span className="inline-flex gap-2 items-center">
                  <button
                    type="button"
                    data-button-type="outline"
                    className="gap-2 items-center"
                    disabled
                  >
                    <SvgIcon
                      src="/img/new-icons/person-add.svg"
                      noFilterOnLight={true}
                    />
                    <span>
                      {referAsClients
                        ? t("teams.inviteClient")
                        : t("teams.inviteAthlete")}
                    </span>
                  </button>
                </span>
              </ToolTip>
            ) : (
              <button
                type="button"
                data-button-type="outline"
                onClick={() => {
                  submit("/actions/refresh", {
                    method: "GET",
                    body: {
                      "modal[type]": "team-invite-clients",
                    },
                  });
                }}
                className="gap-2 items-center"
              >
                <SvgIcon
                  src="/img/new-icons/person-add.svg"
                  noFilterOnLight={true}
                />
                <span>
                  {referAsClients
                    ? t("teams.inviteClient")
                    : t("teams.inviteAthlete")}
                </span>
              </button>
            ))}

          <button data-button-type="secondary">
            {t("teams.publish")}
            <input
              className="ml-2"
              type="checkbox"
              id={`switch-${cycle._id}`}
              name={`publish-cycle`}
              role="switch"
              onChange={(event) => {
                event.preventDefault();
                event.stopPropagation();

                if (event.target.checked) {
                  submit("/actions/refresh", {
                    method: "GET",
                    body: { "modal[type]": "publish-team" },
                  });
                } else {
                  submit("/actions/refresh", {
                    method: "GET",
                    body: { "modal[type]": "unpublish-team" },
                  });
                }
              }}
              checked={cycle.status === "published"}
            />
          </button>
          {teamChatId && (
            <button
              type="button"
              data-button-type="outline"
              className="gap-2 items-center"
              onClick={() => {
                window.location.assign(
                  `/coaching-portal/community-chat?initId=${encodeURIComponent(teamChatId)}`
                );
              }}
            >
              <SvgIcon src="/img/new-icons/chat-gradient.svg" noFilterOnLight />
              <span>{t("teams.goToChat")}</span>
            </button>
          )}
          {createChatButton && !teamChatId && (
            <button
              type="button"
              data-button-type="outline"
              className="gap-2 items-center"
              onClick={() => {
                submit("/actions/refresh", {
                  method: "GET",
                  body: { "modal[type]": "create-chat" },
                });
              }}
            >
              <SvgIcon
                src="/img/new-icons/chat.svg"
                useCurrentColor
                style={{ width: 16, height: 16 }}
              />
              <span>{t("teams.createChat.title")}</span>
            </button>
          )}
          <button
            data-button-type="primary"
            onClick={() => {
              submit(`/actions/cycle/${cycle._id}/teams-add-week`, {
                method: "POST",
                body: { fromTemplates },
              });
            }}
          >
            <img alt={t("teams.addAlt")} src="/img/new-icons/add.svg" />
            {t("teams.addNewWeek")}
          </button>
        </hgroup>
      </header>

      <TeamTabs currentTab={currentTab} />

      <div className="team-tab-container">
        {currentTab === "manager" && (
          <TeamProgramManager locals={locals} api={api} />
        )}
        {currentTab === "information" && <TeamInformation locals={locals} />}
        {currentTab === "members" && <TeamMembers locals={locals} api={api} />}
        {currentTab === "overview" && <TeamOverview locals={locals} />}
      </div>
    </main>
  );
};

export default Teams;
